import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import type { ContentDocument, ContentViewModel } from "../../API/Models";
import { getContentById } from "./contentByIdClient";
import { client } from "../../Clients";
import { showSuccessModal } from "../../Helper/showSuccessModal";
import {
	buildDraftFromContentDoc,
	componentsForPayload,
	normalizeComponents,
	normalizeImageFields,
	safeDecodeSlug
} from "./pageEditorUtils";
import { normalizeStorageKey } from "../../Helper/storage";
import { sanitizePageComponentsForSave, sanitizeParagraphHtml } from "./paragraphHtml";
import { componentDefinitionMap } from "../../Pages/pageComponentLibrary";
import type { AdminPage, PageMetadata } from "./types";
import { normalizeLayoutForSave, type PageLayoutSettings } from "./pageLayoutSettings";

const DEFAULT_LANGUAGE = "fr";
const PAGE_SCHEMA_KEY = "page";

export type UsePagePersistenceArgs = {
	/** The canonical id from the route (hash-based). */
	routeId: string | undefined;
	/** The slug from the route params. */
	routeSlug: string | undefined;
	/** Slug from the query string. */
	slugFromQuery: string;
	/** Prefix from the query string. */
	prefix: string;
	/** Initial language resolved from route/query. */
	initialLanguage: string;
	/** Route language param. */
	lang: string | undefined;
	/** Decoded slug value. */
	slug: string | undefined;
	/** Decoded id value. */
	id: string | undefined;
	/** Whether this is the /new-page route. */
	isNewDocument: boolean;
	/** Counter that triggers a reload. */
	reloadCount: number;
	/** Callback to set the draft (with component keys attached). */
	setDraftWithComponents: (next: AdminPage | null) => void;
	/** Current draft. */
	draft: AdminPage | null;
};

export type UsePagePersistenceReturn = {
	view: ContentViewModel | null;
	setView: (v: ContentViewModel | null) => void;
	loadingView: boolean;
	saving: boolean;
	error: string | null;
	setError: (e: string | null) => void;
	message: string | null;
	setMessage: (m: string | null) => void;
	deleting: boolean;
	restoring: boolean;
	isEditing: boolean;
	setIsEditing: (v: boolean) => void;
	loadPageView: (language: string, slugValue: string) => Promise<ContentViewModel | null>;
	/** Resolves to true when the save succeeded (caller may then end the edit session). */
	persist: () => Promise<boolean>;
	/** Re-reads the current page after an out-of-band change (e.g. a production publish). */
	reloadCurrentPage: () => Promise<void>;
	performDelete: () => Promise<void>;
	handleRestore: () => Promise<void>;
	handleDelete: () => void;
	showDeleteConfirm: boolean;
	setShowDeleteConfirm: (v: boolean) => void;
};

/**
 * All server I/O for the page editor: initial load (by id, by slug, or a blank draft for
 * /new-page), save, soft delete, restore, and the post-publish reload. The draft itself lives
 * in the caller : this hook only ever pushes complete drafts through `setDraftWithComponents`, so
 * runtime component keys get attached in exactly one place.
 *
 * Backend contract worth knowing: pages are keyed by (schemaKey, language, slug) on the API. A
 * save is an upsert PUT against that triple, and the document id is only authoritative in the
 * response : which is why `persist` rebuilds the draft from what the server returned (picking up
 * the assigned id and server-normalized fields) instead of trusting the payload it just sent, and
 * then rewrites the URL to the canonical /page/{id} route.
 */
export function usePagePersistence(args: UsePagePersistenceArgs): UsePagePersistenceReturn {
	const {
		routeId,
		routeSlug,
		slugFromQuery,
		prefix,
		initialLanguage,
		lang,
		slug,
		id,
		isNewDocument,
		reloadCount,
		setDraftWithComponents,
		draft
	} = args;

	const history = useHistory();
	const [view, setView] = useState<ContentViewModel | null>(null);
	const [loadingView, setLoadingView] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [restoring, setRestoring] = useState(false);
	const [isEditing, setIsEditing] = useState(isNewDocument);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const loadPageView = useCallback(
		async (language: string, slugValue: string): Promise<ContentViewModel | null> => {
			const langCode = (language || DEFAULT_LANGUAGE).toLowerCase();
			const normalizedSlug = (safeDecodeSlug(slugValue) ?? slugValue).trim().toLowerCase();
			if (!normalizedSlug) {
				setError("Slug is required to load page content.");
				return null;
			}

			setLoadingView(true);
			setError(null);
			setMessage(null);
			try {
				const response = await client.content.apiContentSchemaKeyLanguageGet(
					PAGE_SCHEMA_KEY,
					langCode,
					normalizedSlug
				);
				const vm: ContentViewModel | null = response.data ?? null;
				setView(vm);
				return vm;
			} catch (err) {
				console.error(err);
				setView(null);
				setError("Unable to load page content.");
				return null;
			} finally {
				setLoadingView(false);
			}
		},
		[]
	);

	// Initialization: three mutually exclusive sources, checked in priority order : an id route
	// (canonical, fast by-id lookup), a slug(+lang) route, or a blank draft seeded from the query
	// prefix/slug for new pages. `reloadCount` is a caller-owned counter whose only job is to
	// force a refetch of the same route after out-of-band changes (e.g. after a restore).
	useEffect(() => {
		if (id) {
			(async () => {
				setLoadingView(true);
				setError(null);
				setMessage(null);
				try {
					// Resolve the page directly by id (O(1)) instead of downloading the whole page
					// collection and filtering client-side : the latter pulled ~5 MB and stalled the
					// editor on "Loading page…" for seconds on every open.
					const doc = await getContentById(id);
					if (!doc) {
						setDraftWithComponents(null);
						setView(null);
						setError("The requested page was not found.");
						return;
					}

					const data = (doc.data ?? {}) as any;
					const slugValue = ((data?.slug as string | undefined) ?? "").trim();
					if (!slugValue) {
						setDraftWithComponents(null);
						setView(null);
						setError("The requested page is missing a slug.");
						return;
					}

					const langCode = (
						(data?.language as string | undefined) ??
						doc.language ??
						initialLanguage
					).toLowerCase();

					const vm = await loadPageView(langCode, slugValue);
					const currentDoc = (vm?.current as ContentDocument) ?? doc;
					const nextDraft = buildDraftFromContentDoc(currentDoc, langCode, DEFAULT_LANGUAGE);
					if (!nextDraft) {
						setDraftWithComponents(null);
						setView(null);
						setError("The requested page could not be loaded.");
						return;
					}

					setDraftWithComponents(nextDraft);
					setView(vm);
					setIsEditing(false);
				} catch (err) {
					console.error(err);
					setError("Unable to load the selected page.");
					setDraftWithComponents(null);
					setView(null);
				} finally {
					setLoadingView(false);
				}
			})();
			return;
		}

		const startSlug = slugFromQuery || slug || prefix || "";
		if (!startSlug) {
			setDraftWithComponents({
				slug: "",
				language: initialLanguage,
				title: "",
				metadata: { title: "", description: "" },
				components: []
			});
			setView(null);
			return;
		}

		if (slug) {
			(async () => {
				const langCode = (lang ?? initialLanguage).toLowerCase();
				const vm = await loadPageView(langCode, startSlug);
				if (!vm) {
					setDraftWithComponents({
						id: undefined,
						slug: safeDecodeSlug(startSlug) ?? startSlug,
						language: langCode,
						title: "",
						metadata: { title: "", description: "" },
						components: []
					});
					setView(null);
					return;
				}

			const nextDraft = buildDraftFromContentDoc(vm.current as ContentDocument | undefined, langCode, DEFAULT_LANGUAGE);
				if (!nextDraft) {
					setDraftWithComponents({
						id: undefined,
						slug: safeDecodeSlug(startSlug) ?? startSlug,
						language: langCode,
						title: "",
						dynamicContentTypeId: null,
						dynamicPreviewSlugs: [],
						dynamicProductionSlugs: [],
						metadata: { title: "", description: "" },
						components: []
					});
					setView(null);
					return;
				}

				setDraftWithComponents(nextDraft);
				setView(vm);
				setIsEditing(false);
			})();
			return;
		}

		setDraftWithComponents({
			id: undefined,
			slug: safeDecodeSlug(startSlug) ?? startSlug,
			language: initialLanguage,
			title: "",
			dynamicContentTypeId: null,
			dynamicPreviewSlugs: [],
			dynamicProductionSlugs: [],
			metadata: { title: "", description: "" },
			components: []
		});
		setView(null);
	}, [id, slugFromQuery, slug, prefix, initialLanguage, lang, loadPageView, setDraftWithComponents, reloadCount]);

	/** Saves the draft. Returns true on success so the caller can end the edit
	 *  session (and reveal « Publier ») only when the save actually went through. */
	const persist = useCallback(async (): Promise<boolean> => {
		if (!draft?.slug || !draft.language) {
			setError("Slug and language are required.");
			return false;
		}
		setSaving(true);
		setError(null);
		setMessage(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const normalizedSlug = (draft.slug ?? "").trim().toLowerCase();
			const normalizedComponents = normalizeComponents(draft.components).map((component) => {
				const definition = componentDefinitionMap.get(component.componentType as any) ?? null;
				if (!definition?.fields?.length) return component;
				return normalizeImageFields(component, definition.fields);
			});
			const sanitizedComponents = sanitizePageComponentsForSave(normalizedComponents) as typeof normalizedComponents;
			const rawMetaImage = (draft.metadata as PageMetadata | undefined)?.image;
			const normalizedMetaImage = typeof rawMetaImage === "string" ? normalizeStorageKey(rawMetaImage) : rawMetaImage;
			const metadataBase = { ...(draft.metadata ?? {}) } as PageMetadata;
			delete (metadataBase as { taxonomyIds?: unknown }).taxonomyIds;
			const normalizedTaxonomyIds = Array.isArray(draft.taxonomyIds)
				? draft.taxonomyIds.filter(Boolean)
				: [];
			const normalizedLayout = normalizeLayoutForSave(draft.layout);
			const payloadPage: AdminPage = {
				...draft,
				slug: normalizedSlug,
				language: langCode,
				title: typeof draft.title === "string" ? sanitizeParagraphHtml(draft.title) : "",
				taxonomyIds: normalizedTaxonomyIds,
				metadata: {
					...metadataBase,
					title: sanitizeParagraphHtml((draft.metadata as PageMetadata | undefined)?.title ?? ""),
					description: (draft.metadata as PageMetadata | undefined)?.description ?? "",
					image: normalizedMetaImage
				},
				layout: normalizedLayout,
				components: componentsForPayload(sanitizedComponents) as AdminPage["components"]
			};

			const payloadDoc: ContentDocument = {
				contentType: PAGE_SCHEMA_KEY,
				language: langCode,
				id: draft.id ?? undefined,
				isDisabled: false,
				isDeleted: false,
				data: payloadPage
			};

			const saveResponse = await client.content.apiContentSchemaKeyLanguagePut(
				PAGE_SCHEMA_KEY,
				langCode,
				normalizedSlug,
				payloadDoc
			);
			const nextVm = saveResponse.data ?? null;
			setView(nextVm);

			// Rebuild the draft from the server's response rather than reusing the local payload:
			// the API normalizes fields (slug casing, dynamic slugs, layout) and assigns the id on
			// first save. Each field still falls back to the payload we sent, so a partial
			// response cannot blank out the editor.
			const savedDoc = nextVm?.current as ContentDocument | undefined;
			const savedData = (savedDoc?.data ?? {}) as any;
			const savedTaxonomyIds = Array.isArray(savedData?.taxonomyIds)
				? (savedData.taxonomyIds as string[]).filter(Boolean)
				: normalizedTaxonomyIds;
			const savedDynamicPreviewSlugs = Array.isArray(savedData?.dynamicPreviewSlugs)
				? (savedData.dynamicPreviewSlugs as string[]).filter(Boolean)
				: payloadPage.dynamicPreviewSlugs ?? [];
			const savedDynamicProductionSlugs = Array.isArray(savedData?.dynamicProductionSlugs)
				? (savedData.dynamicProductionSlugs as string[]).filter(Boolean)
				: payloadPage.dynamicProductionSlugs ?? [];
			const savedDynamicContentTypeId =
				typeof savedData?.dynamicContentTypeId === "string"
					? (savedData.dynamicContentTypeId as string)
					: payloadPage.dynamicContentTypeId ?? null;
			const savedLayoutCandidate = normalizeLayoutForSave(
				(savedData?.layout as PageLayoutSettings | undefined) ?? null
			);
			const savedLayout = savedLayoutCandidate ?? normalizedLayout ?? null;
			const saved: AdminPage = {
				id: savedDoc?.id ?? payloadPage.id ?? draft.id ?? null,
				slug: safeDecodeSlug(savedData?.slug) ?? savedData?.slug ?? payloadPage.slug,
				language: (savedData?.language ?? savedDoc?.language ?? langCode)?.toLowerCase() ?? langCode,
				title: (savedData?.title as string | undefined) ?? payloadPage.title ?? "",
				taxonomyIds: savedTaxonomyIds,
				dynamicContentTypeId: savedDynamicContentTypeId,
				dynamicPreviewSlugs: savedDynamicPreviewSlugs,
				dynamicProductionSlugs: savedDynamicProductionSlugs,
				metadata: (savedData?.metadata as PageMetadata | undefined) ?? payloadPage.metadata ?? {},
				layout: savedLayout,
				components: normalizeComponents(
					(savedData?.components as any[] | undefined) ?? payloadPage.components ?? []
				),
				isDeleted: savedDoc?.isDeleted ?? false,
				previewDeployedAt: savedDoc?.previewDeployedAt ?? null,
				productionDeployedAt: savedDoc?.productionDeployedAt ?? null
			};

			setDraftWithComponents(saved);

			// After a first save (or a save reached via a slug route), move to the id route so a
			// refresh/bookmark goes through the fast by-id loader. replace (not push) keeps the
			// back button pointing at the page list rather than the pre-save URL.
			const canonicalId = saved.id ?? draft.id ?? null;

			if (canonicalId && id !== canonicalId) {
				history.replace(`/page/${encodeURIComponent(canonicalId)}`);
			}
			const successMessage = "Page saved.";
			setMessage(successMessage);
			const cur = nextVm?.current as ContentDocument | undefined;
			const previewOutOfSync =
				!!(cur?.updatedAt && cur?.previewDeployedAt && new Date(cur.updatedAt) > new Date(cur.previewDeployedAt));
			showSuccessModal({
				title: "Page saved",
				message: successMessage,
				note: previewOutOfSync ? "La preview se met à jour automatiquement dans quelques secondes." : undefined,
				// Pour qui detient le droit : enregistrer et mettre en production en un geste.
				publish: canonicalId ? { contentId: canonicalId, contentType: "page" } : undefined
			});
			return true;
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error as string;
			setError(apiMessage ?? "Failed to save the page.");
			return false;
		} finally {
			setSaving(false);
		}
	}, [draft, id, initialLanguage, setDraftWithComponents, history]);

	// Re-reads the current page so the deploy markers (productionDeployedAt & co) reflect an
	// out-of-band change : typically right after « Publier en production » : without a refresh.
	const reloadCurrentPage = useCallback(async () => {
		if (!draft?.slug || !draft.language) return;
		const langCode = (draft.language ?? initialLanguage).toLowerCase();
		const normalizedSlug = (draft.slug ?? "").trim().toLowerCase();
		const vm = await loadPageView(langCode, normalizedSlug);
		const nextDraft = buildDraftFromContentDoc(vm?.current as ContentDocument | undefined, langCode, DEFAULT_LANGUAGE);
		if (vm && nextDraft) {
			setDraftWithComponents(nextDraft);
			setView(vm);
		}
	}, [draft?.slug, draft?.language, initialLanguage, loadPageView, setDraftWithComponents]);

	// Delete is a soft delete: the same PUT as a save, with isDeleted set on both the document and
	// its data. The document stays in the store and can come back via handleRestore : there is no
	// hard DELETE from the editor. Components are sanitized exactly like on save so the archived
	// version remains restorable as-is.
	const performDelete = useCallback(async () => {
		if (!draft?.id || !draft.slug || !draft.language) return;
		setShowDeleteConfirm(false);
		setDeleting(true);
		setError(null);
		setMessage(null);
		try {
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const normalizedSlug = (draft.slug ?? "").trim().toLowerCase();

			const normalizedForDelete = normalizeComponents(draft.components);
			const deleteComponents = componentsForPayload(
				sanitizePageComponentsForSave(normalizedForDelete) as typeof normalizedForDelete
			) as AdminPage["components"];
			const payloadPage: AdminPage = {
				...draft,
				slug: normalizedSlug,
				language: langCode,
				id: draft.id,
				isDeleted: true,
				metadata: {
					...(draft.metadata ?? {}),
					title: sanitizeParagraphHtml(draft.metadata?.title ?? draft.title ?? "")
				},
				components: deleteComponents
			};

			const payloadDoc: ContentDocument = {
				contentType: PAGE_SCHEMA_KEY,
				language: langCode,
				id: draft.id ?? undefined,
				isDisabled: false,
				isDeleted: true,
				data: payloadPage
			};

			await client.content.apiContentSchemaKeyLanguagePut(
				PAGE_SCHEMA_KEY,
				langCode,
				normalizedSlug,
				payloadDoc
			);

			const pageLabel = draft.title?.trim() || draft.slug || "Page";
			showSuccessModal({
				title: "Page deleted",
				message: `${pageLabel} has been deleted.`
			});
			history.push("/pages");
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error as string;
			setError(apiMessage ?? "Failed to delete the page.");
		} finally {
			setDeleting(false);
		}
	}, [draft, initialLanguage, history]);

	const handleDelete = useCallback(() => {
		if (!draft?.id || !draft.slug || !draft.language) return;
		setShowDeleteConfirm(true);
	}, [draft?.id, draft?.slug, draft?.language]);

	const handleRestore = useCallback(async () => {
		if (!draft?.id?.trim()) return;
		const pageId = draft.id.trim();
		setRestoring(true);
		setError(null);
		setMessage(null);
		try {
			await client.content.apiContentPageRestorePost(pageId, { withCredentials: true });
			const slug = (draft.slug ?? "").trim();
			const langCode = (draft.language ?? initialLanguage).toLowerCase();
			const response = await client.content.apiContentSchemaKeyLanguageGet(
				PAGE_SCHEMA_KEY,
				langCode,
				slug
			);
			const vm: ContentViewModel | null = response.data ?? null;
			if (vm?.current) {
				setView(vm);
				const nextDraft = buildDraftFromContentDoc(vm.current as ContentDocument, langCode, DEFAULT_LANGUAGE);
				if (nextDraft) setDraftWithComponents(nextDraft);
			}
			showSuccessModal({
				title: "Page restored",
				message: "This page has been restored. You can publish it to preview again."
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.message ?? err?.response?.data ?? err?.message ?? "Restore failed.";
			setError(String(apiMessage));
		} finally {
			setRestoring(false);
		}
	}, [draft?.id, draft?.slug, draft?.language, initialLanguage, setDraftWithComponents]);

	return {
		view,
		setView,
		loadingView,
		saving,
		error,
		setError,
		message,
		setMessage,
		deleting,
		restoring,
		isEditing,
		setIsEditing,
		loadPageView,
		persist,
		reloadCurrentPage,
		performDelete,
		handleRestore,
		handleDelete,
		showDeleteConfirm,
		setShowDeleteConfirm
	};
}
